import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Shield, ShieldCheck, ShieldOff, QrCode, Key, Copy, Check, 
  Loader2, AlertTriangle, RefreshCw, Smartphone, Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TwoFactorSettings() {
  const { user, getAuthHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showBackupCodesDialog, setShowBackupCodesDialog] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [backupCodesCopied, setBackupCodesCopied] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState([]);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/2fa/status`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setStatus(response.data);
    } catch (error) {
      // 2FA not available for this user type
      setStatus({ is_2fa_enabled: false, available: false });
    } finally {
      setLoading(false);
    }
  };

  const startSetup = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/setup`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setSetupData(response.data);
      setShowSetupDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start 2FA setup');
    } finally {
      setProcessing(false);
    }
  };

  const enable2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setProcessing(true);
    try {
      await axios.post(`${API}/auth/2fa/enable`, { totp_code: verificationCode }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Two-factor authentication enabled!');
      setShowSetupDialog(false);
      setVerificationCode('');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setProcessing(false);
    }
  };

  const disable2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter your current 6-digit code');
      return;
    }

    setProcessing(true);
    try {
      await axios.delete(`${API}/auth/2fa/disable`, {
        data: { totp_code: verificationCode },
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Two-factor authentication disabled');
      setShowDisableDialog(false);
      setVerificationCode('');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setProcessing(false);
    }
  };

  const regenerateBackupCodes = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter your current 6-digit code');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/regenerate-backup-codes`, 
        { totp_code: verificationCode },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setNewBackupCodes(response.data.backup_codes);
      setVerificationCode('');
      toast.success('Backup codes regenerated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate codes');
    } finally {
      setProcessing(false);
    }
  };

  const copyBackupCodes = (codes) => {
    navigator.clipboard.writeText(codes.join('\n'));
    setBackupCodesCopied(true);
    toast.success('Backup codes copied to clipboard');
    setTimeout(() => setBackupCodesCopied(false), 3000);
  };

  // Only show for admin/owner
  const isEligible = user?.is_admin || user?.is_owner;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
        </CardContent>
      </Card>
    );
  }

  if (!isEligible) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>Two-factor authentication is available for admin and owner accounts.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="two-factor-settings">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Two-Factor Authentication
              </CardTitle>
              <CardDescription>
                Add an extra layer of security to your account
              </CardDescription>
            </div>
            <Badge className={status?.is_2fa_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
              {status?.is_2fa_enabled ? (
                <><ShieldCheck className="w-3 h-3 mr-1" /> Enabled</>
              ) : (
                <><ShieldOff className="w-3 h-3 mr-1" /> Disabled</>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {status?.is_2fa_enabled ? (
            <>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-800">
                  <ShieldCheck className="w-5 h-5" />
                  <span className="font-medium">Your account is protected with 2FA</span>
                </div>
                <p className="text-sm text-green-700 mt-1">
                  You'll need to enter a code from your authenticator app when logging in.
                </p>
              </div>

              <div className="flex gap-3 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => setShowBackupCodesDialog(true)}
                  data-testid="regenerate-backup-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Regenerate Backup Codes
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => setShowDisableDialog(true)}
                  data-testid="disable-2fa-btn"
                >
                  <ShieldOff className="w-4 h-4 mr-2" />
                  Disable 2FA
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">2FA is not enabled</span>
                </div>
                <p className="text-sm text-yellow-700 mt-1">
                  We strongly recommend enabling two-factor authentication for admin accounts.
                </p>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">How it works:</h4>
                <ol className="text-sm text-gray-600 space-y-1 ml-4 list-decimal">
                  <li>Download an authenticator app (Google Authenticator, Authy, etc.)</li>
                  <li>Scan the QR code with your authenticator app</li>
                  <li>Enter the 6-digit code to verify and enable 2FA</li>
                  <li>Save your backup codes in a secure place</li>
                </ol>
              </div>

              <Button onClick={startSetup} disabled={processing} className="bg-purple-600 hover:bg-purple-700" data-testid="setup-2fa-btn">
                {processing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Smartphone className="w-4 h-4 mr-2" />
                )}
                Set Up Two-Factor Authentication
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-purple-600" />
              Set Up Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Scan this QR code with your authenticator app
            </DialogDescription>
          </DialogHeader>

          {setupData && (
            <div className="space-y-4">
              {/* QR Code */}
              <div className="flex justify-center">
                <img 
                  src={setupData.qr_code} 
                  alt="2FA QR Code" 
                  className="w-48 h-48 border rounded-lg"
                />
              </div>

              {/* Manual Entry */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Can't scan? Enter this code manually:</p>
                <code className="text-sm font-mono break-all">{setupData.totp_secret}</code>
              </div>

              {/* Backup Codes */}
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-orange-800">Backup Codes (save these!)</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyBackupCodes(setupData.backup_codes)}
                  >
                    {backupCodesCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {setupData.backup_codes.map((code, idx) => (
                    <code key={idx} className="text-xs font-mono bg-white px-2 py-1 rounded">{code}</code>
                  ))}
                </div>
              </div>

              {/* Verification */}
              <div className="space-y-2">
                <Label>Enter the 6-digit code from your app</Label>
                <Input
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSetupDialog(false)}>Cancel</Button>
            <Button onClick={enable2FA} disabled={processing || verificationCode.length !== 6} className="bg-purple-600 hover:bg-purple-700">
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              Enable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldOff className="w-5 h-5" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Enter your current 6-digit code to disable 2FA
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
              <p className="text-sm text-red-700">
                Warning: Disabling 2FA will make your account less secure. Make sure you have a strong password.
              </p>
            </div>
            <Label>Current 2FA Code</Label>
            <Input
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="text-center text-2xl tracking-widest font-mono mt-2"
              maxLength={6}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowDisableDialog(false); setVerificationCode(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={disable2FA} disabled={processing || verificationCode.length !== 6}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Disable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Backup Codes Dialog */}
      <Dialog open={showBackupCodesDialog} onOpenChange={setShowBackupCodesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-purple-600" />
              Regenerate Backup Codes
            </DialogTitle>
            <DialogDescription>
              {newBackupCodes.length > 0 
                ? "Here are your new backup codes. Save them securely!"
                : "Enter your current 2FA code to generate new backup codes"
              }
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {newBackupCodes.length > 0 ? (
              <div className="space-y-3">
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-green-800">New Backup Codes</p>
                    <Button variant="ghost" size="sm" onClick={() => copyBackupCodes(newBackupCodes)}>
                      {backupCodesCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {newBackupCodes.map((code, idx) => (
                      <code key={idx} className="text-xs font-mono bg-white px-2 py-1 rounded">{code}</code>
                    ))}
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  Your old backup codes are no longer valid. Store these new codes in a secure place.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  This will invalidate all your existing backup codes and generate new ones.
                </p>
                <Label>Current 2FA Code</Label>
                <Input
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            {newBackupCodes.length > 0 ? (
              <Button onClick={() => { setShowBackupCodesDialog(false); setNewBackupCodes([]); }}>
                Done
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setShowBackupCodesDialog(false); setVerificationCode(''); }}>Cancel</Button>
                <Button onClick={regenerateBackupCodes} disabled={processing || verificationCode.length !== 6} className="bg-purple-600 hover:bg-purple-700">
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Generate New Codes
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
